import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Activity,
  BarChart3,
  Braces,
  Clock3,
  ExternalLink,
  Link2,
  Mail,
  MailCheck,
  Layers3,
  Map,
  LogOut,
  Menu,
  Settings2,
  ShieldCheck,
  Users,
  X,
} from "lucide-react";
import { accountRoleLabel } from "../lib/format";
import { useSession } from "../context/session";
import { useToast } from "./ui";

const adminItems = [
  { to: "/console/links", label: "短链", en: "links", icon: Link2 },
  { to: "/console/mail", label: "临时邮箱", en: "mail", icon: Mail },
  { to: "/console/mail-admin", label: "邮箱管理", en: "inboxes", icon: MailCheck },
  { to: "/console/mail-domains", label: "邮件域组", en: "domains", icon: Layers3 },
  { to: "/console/review", label: "待审", en: "review", icon: ShieldCheck },
  { to: "/console/pool", label: "号池", en: "pool", icon: Braces },
  { to: "/console/lifecycle", label: "过期与释放", en: "expiry", icon: Clock3 },
  { to: "/console/users", label: "用户", en: "users", icon: Users },
  { to: "/console/analytics", label: "统计", en: "stats", icon: BarChart3 },
  { to: "/console/geography", label: "地域分布", en: "geography", icon: Map },
  { to: "/console/api", label: "API", en: "tokens", icon: Activity },
  { to: "/console/settings", label: "系统设置", en: "settings", icon: Settings2 },
] as const;

const userItems = [
  { to: "/console/links", label: "我的短链", en: "links", icon: Link2 },
  { to: "/console/mail", label: "临时邮箱", en: "mail", icon: Mail },
] as const;

export function AppShell() {
  const { user, config, logout } = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  if (!user) return null;
  const items = user.role === "user" ? userItems : adminItems;

  const signOut = async () => {
    try {
      await logout();
      navigate("/login", { replace: true });
    } catch (error) {
      toast.fromError(error);
    }
  };

  return (
    <div className="app-shell">
      <header className="mobile-bar">
        <button className="icon-button" type="button" onClick={() => setOpen(true)} aria-label="打开导航">
          <Menu size={20} />
        </button>
        <strong className="wordmark">{config?.appName ?? "g2.al"}</strong>
        <span className="eyebrow">CONSOLE</span>
      </header>
      <aside className={`sidebar ${open ? "sidebar--open" : ""}`}>
        <div className="sidebar__brand">
          <div>
            <strong className="wordmark">{config?.appName ?? "g2.al"}</strong>
            <span className="eyebrow">SHORT DOMAIN · {user.role === "user" ? user.tier === "vip" ? "VIP" : "MEMBER" : "ADMIN"}</span>
          </div>
          <button className="icon-button sidebar__close" type="button" onClick={() => setOpen(false)} aria-label="关闭导航">
            <X size={19} />
          </button>
        </div>
        <nav className="sidebar__nav" aria-label="主导航">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => (isActive ? "sidebar-link sidebar-link--active" : "sidebar-link")}
                onClick={() => setOpen(false)}
              >
                <span className="sidebar-link__main"><Icon size={15} />{item.label}</span>
                <span className="eyebrow">{item.en}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="sidebar__utility">
          <a href="/" target="_blank" rel="noreferrer" className="sidebar-utility-link">
            <span>访问短域名</span><ExternalLink size={14} />
          </a>
        </div>
        <div className="sidebar__account">
          <div className="avatar">{user.email.slice(0, 1).toUpperCase()}</div>
          <div className="sidebar__account-copy">
            <strong title={user.email}>{user.email}</strong>
            <span>{accountRoleLabel(user.role, user.tier)} · {location.pathname.split("/").at(-1)}</span>
          </div>
          <button className="icon-button" type="button" onClick={() => void signOut()} aria-label="退出登录">
            <LogOut size={16} />
          </button>
        </div>
      </aside>
      {open ? <button className="sidebar-scrim" type="button" aria-label="关闭导航" onClick={() => setOpen(false)} /> : null}
      <main className="app-main">
        <Outlet />
      </main>
    </div>
  );
}

export function PageHeader({
  eyebrow,
  title,
  body,
  actions,
}: {
  eyebrow: string;
  title: string;
  body?: string;
  actions?: React.ReactNode;
}) {
  return (
    <header className="page-header">
      <div className="page-header__copy">
        <span className="eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        {body ? <p>{body}</p> : null}
      </div>
      {actions ? <div className="page-header__actions">{actions}</div> : null}
    </header>
  );
}
